import React from 'react';
import './Checkbox.css';

export interface WxbCheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'checked' | 'defaultChecked' | 'onChange' | 'type'
> {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
  indeterminate?: boolean;
}

export const WxbCheckbox = React.forwardRef<HTMLInputElement, WxbCheckboxProps>(({
  checked: controlledChecked,
  defaultChecked = false,
  onChange,
  disabled = false,
  indeterminate = false,
  children,
  className = '',
  id,
  ...inputProps
}, forwardedRef) => {
  const autoId = React.useId();
  const checkboxId = id || `wxb-checkbox-${autoId.replace(/:/g, '')}`;
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [internal, setInternal] = React.useState(defaultChecked);
  const isControlled = controlledChecked !== undefined;
  const isChecked = isControlled ? controlledChecked : internal;

  const setInputRef = React.useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }, [forwardedRef]);

  React.useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const next = event.target.checked;
    if (!isControlled) setInternal(next);
    onChange?.(next, event);
  }, [disabled, isControlled, onChange]);

  const handleLabelClick = React.useCallback((event: React.MouseEvent<HTMLLabelElement>) => {
    event.stopPropagation();
  }, []);

  return (
    <label
      className={`wxb-checkbox ${isChecked ? 'is-checked' : ''} ${indeterminate ? 'is-indeterminate' : ''} ${disabled ? 'is-disabled' : ''} ${className}`}
      htmlFor={checkboxId}
      onClick={handleLabelClick}
    >
      <input
        {...inputProps}
        ref={setInputRef}
        id={checkboxId}
        className="wxb-checkbox-input"
        type="checkbox"
        checked={isChecked}
        disabled={disabled}
        aria-checked={indeterminate ? 'mixed' : isChecked}
        onChange={handleChange}
      />
      <span className="wxb-checkbox-box" aria-hidden="true">
        {isChecked && !indeterminate && (
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path d="M2.5 6l2.5 2.5 4.5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {indeterminate && (
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path d="M3 6h6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        )}
      </span>
      {children && <span className="wxb-checkbox-label">{children}</span>}
    </label>
  );
});

WxbCheckbox.displayName = 'WxbCheckbox';
